import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { FaEdit, FaCog, FaSave, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PageForm from '../../templates/PageForm';
import TextEdit from '../../molecules/TextEdit';
import NewTooltip from '../../atoms/NewTooltip';
import FieldMappingModal from './FieldMappingModal';

const API_KEY = process.env.REACT_APP_API_KEY || '';
const BASE_URL = `${process.env.REACT_APP_DATA_PROVIDER_API_URL}/report-types`;

interface ReportType {
    id: string;
    name: string;
    provider: string;
    documentType: string;
    description: string;
    params: Record<string, string>;
    fields: any[];
    status: boolean;
}

const ReportTypePageForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [reportType, setReportType] = useState<ReportType | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [editedParams, setEditedParams] = useState<Record<string, string>>({});
    const [hasChanges, setHasChanges] = useState(false);
    const showFieldModal = useSignal(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${BASE_URL}/${id}/page-view`, {
                    headers: {
                        'x-api-key': API_KEY || '',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    const newReportType = {
                        id: data.id,
                        name: data.name,
                        provider: data.provider,
                        documentType: data.documentType,
                        description: data.description,
                        params: data.params || {},
                        fields: data.fields || [],
                        status: data.active
                    };
                    setReportType(newReportType);
                    setEditedParams(data.params || {});
                } else if (response.status === 401) {
                    navigate('/denied');
                } else {
                    navigate('/network-error');
                }
            } catch (error) {
                navigate('/network-error');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [id, navigate]);

    const handleSave = () => {
        window.location.reload();
    };

    const handleParamChange = (key: string, value: string) => {
        setEditedParams(prev => ({ ...prev, [key]: value }));
        setHasChanges(true);
    };

    const saveParams = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    params: editedParams
                })
            });

            if (response.ok) {
                toast.success('Parâmetros salvos com sucesso!');
                setHasChanges(false);
                navigate(-1);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao salvar parâmetros');
            }
        } catch (error) {
            toast.error('Erro de conexão ao salvar parâmetros');
        }
    };

    if (isLoading) {
        return (
            <PageForm
                pageTitle="Carregando..."
                apiVersion=""
                isLoading={true}
                breadcrumb={[
                    { label: 'Tipos de relatório', url: '/data-provider/report-type/0/20/id/ASC/' },
                    { label: 'Editar', url: '#' }
                ]}
                controlSlot={<div />}
            >
                <div />
            </PageForm>
        );
    }

    return (
        <>
            <PageForm
                pageTitle={`Editar: ${reportType?.name || ''}`}
                apiVersion=""
                isLoading={false}
                breadcrumb={[
                    { label: 'Tipos de relatório', url: '/data-provider/report-type/0/20/id/ASC/' },
                    { label: 'Editar', url: '#' }
                ]}
                controlSlot={(
                    <div className="flex space-x-2 justify-end">
                        <button
                            className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded"
                            onClick={() => navigate(-1)}
                        >
                            <IoMdArrowRoundBack />
                        </button>
                        <NewTooltip content="Mapeamento de Campos" alignment="bottom">
                            <button
                                className="bg-green-600 text-white p-2 rounded hover:bg-green-800"
                                onClick={() => showFieldModal.value = true}
                            >
                                <FaCog />
                            </button>
                        </NewTooltip>
                        {(hasChanges || true) && (
                            <NewTooltip content="Salvar Parâmetros" alignment="bottom">
                                <button
                                    className="bg-blue-500 text-white p-2 rounded hover:bg-blue-700"
                                    onClick={saveParams}
                                >
                                    Salvar
                                </button>
                            </NewTooltip>
                        )}
                    </div>
                )}
            >
                {reportType && (
                    <div className="p-4 space-y-6">
                        <div className="grid grid-cols-3 gap-4">
                            <TextEdit
                                id="name"
                                label="Nome"
                                value={reportType.name}
                                disabled
                            />
                            <TextEdit
                                id="provider"
                                label="Provedor"
                                value={reportType.provider}
                                disabled
                            />
                            <TextEdit
                                id="documentType"
                                label="Tipo de Documento"
                                value={reportType.documentType}
                                disabled
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <TextEdit
                                id="description"
                                label="Descrição"
                                value={reportType.description}
                                disabled
                            />
                            <div className="flex flex-col">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Status
                                </label>
                                <span className={`px-3 py-2 rounded text-sm font-medium w-fit ${reportType.status
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                    }`}>
                                    {reportType.status ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                                Parâmetros
                            </h3>
                            <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                                {Object.keys(reportType.params).length > 0 ? (
                                    <div className="grid grid-cols-1 gap-3">
                                        {Object.entries(reportType.params).map(([key, value]) => (
                                            <div key={key} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-600 last:border-b-0">
                                                <span className="font-medium text-gray-700 dark:text-gray-300 w-1/3">{key}:</span>
                                                <input
                                                    type="text"
                                                    value={editedParams[key] || ''}
                                                    onChange={(e) => handleParamChange(key, e.target.value)}
                                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder={`Valor para ${key}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 dark:text-gray-400 py-4">
                                        Nenhum parâmetro configurado
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </PageForm>

            <FieldMappingModal
                show={showFieldModal.value}
                reportType={reportType}
                onClose={() => showFieldModal.value = false}
                onSave={handleSave}
            />
        </>
    );
};

export default ReportTypePageForm;
